import { observer } from 'mobx-react';
import React, { Suspense, useContext, useLayoutEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import PublicHeader from 'scenes/PublicHeader';
import PublicFooter from 'scenes/PublicFooter';
import LoadingRing from 'presentation/LoadingRing';
import PublicMainLayout from 'presentation/PublicMainLayout';
import lazyLoaderComponent from 'util/lazyLoaderComponent';
import { CustomerSelfManagementStoreContext } from 'providers/PublicStoreProvider';

import * as Routes from 'routing/routes';

const CustomerSelfManagementHub = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "CustomerSelfManagementHubComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/CustomerSelfManagementHub'
    )
);

const Accidents = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "AccidentsComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/Accidents'
    )
);

const Documents = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "DocumentsComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/Documents'
    )
);

const ChangePaymentMethod = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "ChangePaymentMethodComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/ChangePaymentMethod'
    )
);

const ModifyPolicyData = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "ModifyPolicyDataComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/ModifyPolicyData'
    )
);

const Inspections = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "InspectionsComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/Inspections'
    )
);

const PaymentRejection = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "PaymentRejectionComponent" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/CustomerSelfManagement/PaymentRejection'
    )
);

const NotFound404 = lazyLoaderComponent(() =>
    import(
        /* webpackChunkName: "NotFound404Component" */
        /* webpackMode: "lazy" */
        /* webpackExports: ["default"] */
        'scenes/NotFound404'
    )
);

const PublicRouter = () => {
    const customerSelfManagementStore = useContext(CustomerSelfManagementStoreContext);
    const location = useLocation();

    useLayoutEffect(() => {
        customerSelfManagementStore.setPlatformByPath(location.pathname);
    }, [
        customerSelfManagementStore, 
        location.pathname, 
        customerSelfManagementStore.platforms
    ]);

    return (
        <PublicMainLayout header={PublicHeader} footer={PublicFooter}>
            <Suspense fallback={<LoadingRing center />}>
                <Switch>
                    {customerSelfManagementStore.platforms?.flatMap((platform) => [
                        <Route key={`${platform.name}-main`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}`} component={CustomerSelfManagementHub} />,
                        <Route key={`${platform.name}-clients`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_CLIENTS}`} component={CustomerSelfManagementHub} />,
                        <Route key={`${platform.name}-documents`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_DOCUMENTS}`} component={Documents} />,
                        <Route key={`${platform.name}-accidents`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_ACCIDENTS}`} component={Accidents} />,
                        <Route key={`${platform.name}-inspections`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_INSPECTIONS}`} component={Inspections} />,
                        <Route key={`${platform.name}-accident-details`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_ACCIDENTS_DETAIL}`} component={Accidents} />,
                        <Route key={`${platform.name}-payment-method`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_CHANGE_PAYMENT_METHOD}`} component={ChangePaymentMethod} />,
                        <Route key={`${platform.name}-policy-data`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_MODIFY_POLICY_DATA}`} component={ModifyPolicyData} />,
                        <Route key={`${platform.name}-payment-rejections`} exact path={`${Routes.CUSTOMER_SELF_MANAGEMENT}/${platform.name}${Routes.CUSTOMER_SELF_MANAGEMENT_PAYMENT_REJECTIONS}`} component={PaymentRejection} />,
                    ])}
                    <Route component={NotFound404} />
                </Switch>
            </Suspense>
        </PublicMainLayout>
    );
};

export default observer(PublicRouter);
